import _ from 'lodash'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { UserFeature } from '@briq/core'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../components/Button'
import { Space } from '../../components/Space'
import { ColumnType } from '../../components/Table'
import { Text } from '../../components/Text'
import { useLinkGenerator } from '../../hooks/useLinkGenerator'
import { getPricesMode, getShowAiChatbotButton, getViewHtmlIcon, PricesMode } from '../../redux/config'
import { DomainsActions, DomainType } from '../../redux/domains'
import { getDomain } from '../../redux/domains/selectors'
import { ContractType } from '../../redux/user'
import { canSeePrices, getContractType } from '../../redux/user/selectors'
import { Routes } from '../../routes/routes'
import { Dictionary } from '../../utils/types'
import { useViewportSize } from '../../hooks/useViewportSize'
import { useAuthorizations } from '../../hooks'

export enum ArchiveReportColumnRender {
  Date = 'Date',
  DialogOpen = 'DialogOpen',
  Domain = 'Domain',
  UmbracoProductName = 'UmbracoProductName',
  Country = 'Country',
  DownloadIcon = 'DownloadIcon',
  ViewIcon = 'ViewIcon',
  AiIcon = 'AiIcon'
}

type CustomColumn = ColumnType<any> & {
  renderType?: ArchiveReportColumnRender
  renderParams?: Dictionary<unknown>
}

export enum ReportFieldValueType {
  String = 'string',
  Date = 'date',
  Currency = 'currency',
  Domain = 'Domain'
}

export type ReportFieldValue = {
  domain?: DomainType
  label: string
  prop: string
  valueType: ReportFieldValueType
}

const RenderEmptyTable = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('search')
  const { generateRoute } = useLinkGenerator()

  return (
    <Space
      direction="vertical"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      space={3}
      padding="50px 10%"
    >
      <Text color="darkGray">{t('search:NotFound.Title')}</Text>
      <Text color="darkGray">{t('search:NotFound.Text')}</Text>
      <Button
        onClick={() => {
          navigate(generateRoute(Routes.Investigation))
        }}
        variant="primary"
      >
        {t('search:NotFound.Cta')}
      </Button>
    </Space>
  )
}

export type ArchiveReportPageConfig = {
  generateColumns: () => CustomColumn[]
  generateCompletedFields: () => ReportFieldValue[]
  generateNotCompletedFields: () => ReportFieldValue[]
  generateExportFields: () => ReportFieldValue[]

  EmptyTableComponent?: () => React.ReactNode
}

function insertPriceFields(
  position: number,
  pricesMode: PricesMode,
  contractType: ContractType,
  fields: { label: string; prop: string; valueType: ReportFieldValueType; domain?: DomainType }[],
  t: any
) {
  switch (pricesMode) {
    case PricesMode.WithTaxInfo:
      fields.splice(
        position,
        0,
        {
          label: t('form:PriceVatExcluded'),
          prop: 'displayPrice',
          valueType: ReportFieldValueType.Currency
        },
        {
          label: t('form:PriceVatIncluded'),
          prop: 'displayPriceVat',
          valueType: ReportFieldValueType.Currency
        }
      )
      break
    case PricesMode.DecideByContractType:
      if (contractType == ContractType.PayperClick) {
        fields.splice(2, 0, {
          label: t('core:Price'),
          prop: 'displayPriceVat',
          valueType: ReportFieldValueType.Currency
        })
      } else {
        fields.splice(2, 0, {
          label: t('core:Price'),
          prop: 'displayPrice',
          valueType: ReportFieldValueType.Currency
        })
      }
      break
    default:
      fields.splice(2, 0, {
        label: t('core:Price'),
        prop: 'displayPriceVat',
        valueType: ReportFieldValueType.Currency
      })
  }
}

export function useDefaultArchiveReportsPageConfig(): ArchiveReportPageConfig {
  const { t, i18n } = useTranslation(['form', 'archive'])
  const dispatch = useDispatch()

  const adminxVisibility = useSelector(UserFeature.UserSelectors.getAdminxVisibilityOption)
  const showPrices = useSelector(canSeePrices)
  const pricesMode = useSelector(getPricesMode)
  const contractType = useSelector(getContractType)
  const viewPortSize = useViewportSize()
  const viewHtmlIcon = useSelector(getViewHtmlIcon)
  const viewHtmReportIcon = viewPortSize?.viewportWidth && viewPortSize?.viewportWidth < 800 ? false : viewHtmlIcon
  const showAiButton = useSelector(getShowAiChatbotButton)
  const paymentOptionCode = useSelector(UserFeature.UserSelectors.getPaymentOptionCode)
  const { isAuthorizedForOrdering } = useAuthorizations()

  const showAi = showAiButton && paymentOptionCode !== 'PPR' && isAuthorizedForOrdering

  useEffect(() => {
    dispatch(
      DomainsActions.loadDomains({
        domainTypes: _.compact([DomainType.CustomerRequestStatus, showAi ? DomainType.OrderSource : undefined]),
        language: i18n.language
      })
    )
  }, [dispatch, i18n.language, showAi])

  const crsDomain = useSelector((state: any) =>
    getDomain(state, { domain: DomainType.CustomerRequestStatus, language: i18n.language })
  )

  const orderSourceDomain = useSelector((state: any) =>
    getDomain(state, { domain: DomainType.OrderSource, language: i18n.language })
  )

  return {
    EmptyTableComponent: RenderEmptyTable,
    generateColumns: (): CustomColumn[] => {
      const columns: CustomColumn[] = [
        {
          defaultHidden: !viewHtmReportIcon,
          id: 'viewIcon',
          dataIndex: 'viewIcon',
          width: '24px',
          visibilityType: 'non-selectable',
          renderType: ArchiveReportColumnRender.ViewIcon
        },
        {
          id: 'icon',
          dataIndex: 'icon',
          width: '24px',
          visibilityType: 'non-selectable',
          renderType: ArchiveReportColumnRender.DownloadIcon
        },
        {
          id: 'company',
          title: t('form:Company'),
          dataIndex: 'company',
          sorter: true,
          visibilityType: 'fixed',
          renderType: ArchiveReportColumnRender.DialogOpen
        },
        {
          id: 'productName',
          title: t('form:ReportName'),
          dataIndex: 'productCode',
          sorter: true,
          renderType: ArchiveReportColumnRender.UmbracoProductName
        },
        {
          id: 'statusCode',
          title: t('form:OrderStatus'),
          dataIndex: 'statusCode',
          sorter: true,
          renderType: ArchiveReportColumnRender.Domain,
          renderParams: {
            domainData: crsDomain
          }
        },
        {
          id: 'orderDate',
          title: t('form:OrderDate'),
          dataIndex: 'orderDate',
          sorter: true,
          renderType: ArchiveReportColumnRender.Date
        },
        {
          id: 'expirationDate',
          title: t('form:ExpirationDate'),
          dataIndex: 'expirationDate',
          sorter: true,
          renderType: ArchiveReportColumnRender.Date
        },
        // { id: 'country', title: t('form:Country'), dataIndex: 'country', sorter: true },
        {
          id: 'consumerReference',
          title: t('form:CustomerReference'),
          dataIndex: 'consumerReference',
          sorter: true,
          defaultHidden: true
        }
      ]

      if (showAi) {
        columns.push({
          id: 'orderSourceCode',
          dataIndex: 'orderSourceCode',
          title: t('archive:Source'),
          sorter: true,
          renderType: ArchiveReportColumnRender.Domain,
          renderParams: {
            domainData: orderSourceDomain
          }
        })

        columns.push({
          id: 'aiButton',
          dataIndex: 'aiButton',
          width: '24px',
          visibilityType: 'non-selectable',
          fixed: 'right',
          renderType: ArchiveReportColumnRender.AiIcon
        })
      }

      if (
        adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilityOffice ||
        adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilitySubscriber
      ) {
        columns.push({
          id: 'userCode',
          title: t('form:User'),
          dataIndex: 'userCode',
          sorter: false,
          defaultHidden: true
        })
      }

      if (adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilitySubscriber) {
        columns.push({
          id: 'officeCode',
          title: t('form:Office'),
          dataIndex: 'officeCode',
          sorter: false,
          defaultHidden: true
        })
      }

      columns.push({
        id: 'arrowIcon',
        dataIndex: 'arrowIcon',
        width: '24px',
        visibilityType: 'non-selectable',
        fixed: 'right',
        renderType: ArchiveReportColumnRender.DialogOpen
      })

      return columns
    },
    generateCompletedFields: () => {
      const fields = [
        {
          label: t('form:Company'),
          prop: 'company',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:Status'),
          prop: 'statusCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.CustomerRequestStatus
        },
        {
          label: t('form:Country'),
          prop: 'countryCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.Country
        },
        {
          label: t('form:Language'),
          prop: 'languageCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.Culture
        },
        {
          label: t('form:OrderId'),
          prop: 'orderId',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:RequestID'),
          prop: 'externalSystemId',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:OrderDate'),
          prop: 'orderDate',
          valueType: ReportFieldValueType.Date
        },
        {
          label: t('form:DeliveryDate'),
          prop: 'deliveryDate',
          valueType: ReportFieldValueType.Date
        },
        {
          label: t('form:ExpirationDate'),
          prop: 'expirationDate',
          valueType: ReportFieldValueType.Date
        },
        {
          label: t('form:CustomerReference'),
          prop: 'customerReference',
          valueType: ReportFieldValueType.String
        }
      ]

      if (showAi) {
        fields.push({
          label: t('archive:Source'),
          prop: 'orderSourceCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.OrderSource
        })
      }

      if (
        adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilityOffice ||
        adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilitySubscriber
      ) {
        fields.push({
          prop: 'userCode',
          label: t('form:User'),
          valueType: ReportFieldValueType.String
        })
      }

      if (adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilitySubscriber) {
        fields.push({
          prop: 'officeCode',
          label: t('form:Office'),
          valueType: ReportFieldValueType.String
        })
      }

      insertPriceFields(2, pricesMode, contractType, fields, t)

      if (!showPrices) {
        return _.filter(fields, x => x.valueType !== ReportFieldValueType.Currency)
      }
      return fields
    },
    generateNotCompletedFields: () => {
      const fields = [
        {
          label: t('form:Company'),
          prop: 'company',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:Status'),
          prop: 'statusCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.CustomerRequestStatus
        },
        {
          label: t('form:Country'),
          prop: 'countryCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.Country
        },
        {
          label: t('form:Language'),
          prop: 'languageCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.Culture
        },
        {
          label: t('form:DeliveryOption'),
          prop: 'deliveryOptionCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.DeliveryOption
        },
        {
          label: t('form:OrderId'),
          prop: 'orderId',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:RequestID'),
          prop: 'externalSystemId',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:OrderDate'),
          prop: 'orderDate',
          valueType: ReportFieldValueType.Date
        },
        {
          label: t('form:CustomerReference'),
          prop: 'customerReference',
          valueType: ReportFieldValueType.String
        }
      ]

      if (showAi) {
        fields.push({
          label: t('archive:Source'),
          prop: 'orderSourceCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.OrderSource
        })
      }

      if (
        adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilityOffice ||
        adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilitySubscriber
      ) {
        fields.push({
          prop: 'userCode',
          label: t('form:User'),
          valueType: ReportFieldValueType.String
        })
      }

      if (adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilitySubscriber) {
        fields.push({
          prop: 'officeCode',
          label: t('form:Office'),
          valueType: ReportFieldValueType.String
        })
      }

      insertPriceFields(2, pricesMode, contractType, fields, t)

      if (!showPrices) {
        return _.filter(fields, x => x.valueType !== ReportFieldValueType.Currency)
      }
      return fields
    },
    generateExportFields: () => {
      const fields = [
        {
          label: t('form:Company'),
          prop: 'company',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:ReportName'),
          prop: 'productCode',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:Status'),
          prop: 'statusCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.CustomerRequestStatus
        },
        {
          label: t('form:OrderDate'),
          prop: 'orderDate',
          valueType: ReportFieldValueType.Date
        },
        {
          label: t('form:DeliveryDate'),
          prop: 'deliveryDate',
          valueType: ReportFieldValueType.Date
        },
        {
          label: t('form:ExpirationDate'),
          prop: 'expirationDate',
          valueType: ReportFieldValueType.Date
        },
        {
          label: t('form:CustomerReference'),
          prop: 'customerReference',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:Country'),
          prop: 'countryCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.Country
        },
        {
          label: t('form:Language'),
          prop: 'languageCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.Culture
        },
        {
          label: t('form:DeliveryOption'),
          prop: 'deliveryOptionCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.DeliveryOption
        },
        {
          label: t('form:OrderId'),
          prop: 'orderId',
          valueType: ReportFieldValueType.String
        },
        {
          label: t('form:RequestID'),
          prop: 'externalSystemId',
          valueType: ReportFieldValueType.String
        }
      ]

      if (showAi) {
        fields.push({
          label: t('archive:Source'),
          prop: 'orderSourceCode',
          valueType: ReportFieldValueType.Domain,
          domain: DomainType.OrderSource
        })
      }

      if (
        adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilityOffice ||
        adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilitySubscriber
      ) {
        fields.push({
          prop: 'userCode',
          label: t('form:User'),
          valueType: ReportFieldValueType.String
        })
      }

      if (adminxVisibility === UserFeature.AdminxVisibilityOption.ReportVisibilitySubscriber) {
        fields.push({
          prop: 'officeCode',
          label: t('form:Office'),
          valueType: ReportFieldValueType.String
        })
      }

      insertPriceFields(7, pricesMode, contractType, fields, t)
      if (!showPrices) {
        return _.filter(fields, x => x.valueType !== ReportFieldValueType.Currency)
      }

      return fields
    }
  }
}

export type ArchiveReportPageConfigGenerator = typeof useDefaultArchiveReportsPageConfig
